import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, ArrowRight, Save, Edit2, Plus } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import StatusBadge from "@/components/masterOrder/StatusBadge";
import { getClient, updateClient, listClientOrders } from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";
import { Field, Input } from "@/pages/masterOrder/MOClients";

function ReadRow({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>{label}</div>
      <div className="mt-0.5 text-[13.5px]" style={{ color: "#2A1D14" }}>{value || <span style={{ color: "#B9A692" }}>—</span>}</div>
    </div>
  );
}

export default function MOClientDetail() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      setLoading(true);
      try {
        const c = await getClient(clientId);
        setClient(c);
        setForm({
          name: c.name || "", phone: c.phone || "", email: c.email || "",
          messenger: c.messenger || "", preferred_contact_channel: c.preferred_contact_channel || "",
          default_address: c.default_address || "", source: c.source || "",
          restrictions: c.restrictions || "", notes: c.notes || "",
          tagsCsv: (c.tags || []).join(", "),
        });
        try { setOrders(await listClientOrders(clientId)); } catch { setOrders([]); }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, [clientId, aLoad, mode]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone.trim(),
        email: form.email.trim() || null, messenger: form.messenger.trim() || null,
        preferred_contact_channel: form.preferred_contact_channel.trim() || null,
        default_address: form.default_address.trim() || null,
        source: form.source.trim() || null, restrictions: form.restrictions.trim() || null,
        notes: form.notes.trim() || null,
        tags: form.tagsCsv.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const c = await updateClient(clientId, payload);
      setClient(c);
      setEditing(false);
      toast.success("Изменения сохранены");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <MOShell active="clients" title={client?.name || "Клиент"} subtitle="Карточка клиента">
      {aLoad ? (
        <LoadingBlock />
      ) : mode === "no_access" || mode === "demo" ? (
        <NoAccessScreen />
      ) : loading ? (
        <LoadingBlock />
      ) : notFound || !client ? (
        <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <div className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Клиент не найден</div>
          <button className="mt-4 h-10 px-4 rounded-xl text-[13px]"
                  style={{ background: "#4A3728", color: "#F7EFE0" }} onClick={() => nav("/master-order/clients")}>
            К списку клиентов
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
          {/* Left column: contacts / details / edit */}
          <div className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-client-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Клиент</div>
                <div className="mt-0.5 text-[24px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  {client.name}
                </div>
              </div>
              {canWrite && !editing && (
                <button onClick={() => setEditing(true)}
                        className="h-9 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                        style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                        data-testid="mo-client-edit">
                  <Edit2 size={12} strokeWidth={1.8} /> Редактировать
                </button>
              )}
            </div>

            <div className="mt-4">
              {!editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ReadRow label="Телефон" value={client.phone} />
                  <ReadRow label="Email" value={client.email} />
                  <ReadRow label="Мессенджер" value={client.messenger} />
                  <ReadRow label="Предпочитаемая связь" value={client.preferred_contact_channel} />
                  <ReadRow label="Адрес по умолчанию" value={client.default_address} />
                  <ReadRow label="Источник" value={client.source} />
                  <div className="md:col-span-2"><ReadRow label="Ограничения" value={client.restrictions} /></div>
                  <div className="md:col-span-2"><ReadRow label="Заметка" value={client.notes} /></div>
                  <div className="md:col-span-2">
                    <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Теги</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(client.tags || []).length ? client.tags.map((t) => (
                        <span key={t} className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#5B4326" }}>{t}</span>
                      )) : <span style={{ color: "#B9A692", fontSize: 13 }}>—</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Имя"><Input value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="mo-client-edit-name" /></Field>
                  <Field label="Телефон"><Input value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} testid="mo-client-edit-phone" /></Field>
                  <Field label="Email"><Input type="email" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} /></Field>
                  <Field label="Мессенджер"><Input value={form.messenger} onChange={(v) => setForm(f => ({ ...f, messenger: v }))} /></Field>
                  <Field label="Предпочитаемая связь"><Input value={form.preferred_contact_channel} onChange={(v) => setForm(f => ({ ...f, preferred_contact_channel: v }))} /></Field>
                  <Field label="Адрес"><Input value={form.default_address} onChange={(v) => setForm(f => ({ ...f, default_address: v }))} /></Field>
                  <Field label="Источник"><Input value={form.source} onChange={(v) => setForm(f => ({ ...f, source: v }))} /></Field>
                  <Field label="Теги (через запятую)"><Input value={form.tagsCsv} onChange={(v) => setForm(f => ({ ...f, tagsCsv: v }))} /></Field>
                  <div className="md:col-span-2"><Field label="Ограничения">
                    <textarea value={form.restrictions || ""} onChange={(e) => setForm(f => ({ ...f, restrictions: e.target.value }))}
                              className="w-full h-20 p-3 rounded-lg text-[13px] outline-none resize-none"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              placeholder="Например: без яиц, без алкоголя, без красного декора"
                              data-testid="mo-client-edit-restrictions" />
                    <div className="mt-1 text-[11px] leading-snug" style={{ color: "#9A5F45" }}>
                      Не указывайте диагнозы, сведения о здоровье и иную медицинскую информацию.
                    </div>
                  </Field></div>
                  <div className="md:col-span-2"><Field label="Заметка">
                    <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                              className="w-full h-20 p-3 rounded-lg text-[13px] outline-none resize-none"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  </Field></div>
                  <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                    <button onClick={() => setEditing(false)} className="h-10 px-4 rounded-xl text-[12.5px]"
                            style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
                      Отменить
                    </button>
                    <button onClick={save} disabled={saving}
                            className="h-10 px-5 rounded-xl text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60"
                            style={{ background: "#4A3728", color: "#F7EFE0" }}
                            data-testid="mo-client-save-edit">
                      <Save size={13} strokeWidth={1.8} /> {saving ? "Сохраняем..." : "Сохранить"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: history + quick actions */}
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <div className="flex items-center justify-between mb-2">
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: "#2A1D14" }}>Быстрые действия</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {canWrite && (
                  <button onClick={() => nav(`/master-order/orders/new?clientId=${client.id}`)}
                          className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                          style={{ background: "#4A3728", color: "#F7EFE0" }}
                          data-testid="mo-client-new-order">
                    <Plus size={13} strokeWidth={1.8} /> Новый заказ
                  </button>
                )}
                {client.phone && (
                  <a href={`tel:${client.phone}`} className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                     style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}>
                    <Phone size={13} strokeWidth={1.8} /> Позвонить
                  </a>
                )}
                {client.email && (
                  <a href={`mailto:${client.email}`} className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                     style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}>
                    <Mail size={13} strokeWidth={1.8} /> Email
                  </a>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-client-orders">
              <div className="flex items-center justify-between mb-3">
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: "#2A1D14" }}>История заказов</div>
                <span className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
                  {orders.length}
                </span>
              </div>
              {orders.length === 0 ? (
                <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Пока нет заказов у этого клиента.</div>
              ) : (
                <div className="space-y-1.5">
                  {orders.map((o) => (
                    <button key={o.id} onClick={() => nav(`/master-order/orders/${o.id}`)}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-black/5 transition text-left"
                            data-testid={`mo-client-order-row-${o.id}`}>
                      <div className="text-[11px] tracking-[0.18em] tabular-nums shrink-0"
                           style={{ color: "#8B6F4B" }}>№ {displayOrderNumber(o.order_number)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] leading-tight truncate" style={{ color: "#2A1D14" }}>
                          {o.event_date} {o.event_time || ""}
                        </div>
                        <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                          {o.currency_code}{o.budget ? ` · ${o.budget}` : ""}
                        </div>
                      </div>
                      <StatusBadge status={o.business_status} />
                      <ArrowRight size={13} color="#8B6F4B" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </MOShell>
  );
}
